import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Stack,
  Avatar,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Upload as UploadIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { supabase } from '../../lib/supabaseClient';
import { getAuthUser } from '../../services/authService';
import { validatePassword, getPasswordRequirements } from '../../lib/passwordValidation';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'hr' | 'recruiter';
  entityId: string;
  entityName: string;
  avatarUrl?: string;
  createdAt?: string;
  lastLoginAt?: string;
}

interface ActivityStats {
  jobsCreated: number;
  applicationsReviewed: number;
  usersManaged: number;
}

export const ProfilePage: React.FC = () => {
  const theme = useTheme();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [stats, setStats] = useState<ActivityStats>({ jobsCreated: 0, applicationsReviewed: 0, usersManaged: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');

  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadProfile();
    loadActivityStats();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const user = await getAuthUser();

      if (!user) {
        setError('You must be logged in');
        return;
      }

      // Get entity name
      const { data: entityData } = await supabase
        .from('entities')
        .select('name')
        .eq('id', user.entityId)
        .single();

      const userProfile: UserProfile = {
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        entityId: user.entityId,
        entityName: entityData?.name || 'Unknown',
        avatarUrl: user.avatarUrl,
        createdAt: user.createdAt,
        lastLoginAt: user.lastLoginAt
      };

      setProfile(userProfile);
      setFormData({
        name: userProfile.name,
        email: userProfile.email
      });

      if (userProfile.avatarUrl) {
        setAvatarPreview(userProfile.avatarUrl);
      }

      setError('');
    } catch (err: any) {
      console.error('Error loading profile:', err);
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadActivityStats = async () => {
    try {
      const user = await getAuthUser();
      if (!user) return;

      // Count jobs created by this user
      const { count: jobsCount } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', user.id);

      // Count applications reviewed by this user
      const { count: appsCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('reviewed_by', user.id);

      // Count users managed (only for admin/HR)
      let usersCount = 0;
      if (user.role === 'admin' || user.role === 'hr') {
        const { count } = await supabase
          .from('users')
          .select('*', { count: 'exact', head: true })
          .eq('entity_id', user.entityId);
        usersCount = count || 0;
      }

      setStats({
        jobsCreated: jobsCount || 0,
        applicationsReviewed: appsCount || 0,
        usersManaged: usersCount
      });
    } catch (err: any) {
      console.error('Error loading stats:', err);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Avatar file size must be less than 2MB');
        return;
      }

      setAvatarFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile || !profile) return null;

    try {
      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `${profile.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('entity-assets')
        .upload(filePath, avatarFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('entity-assets')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (err: any) {
      console.error('Error uploading avatar:', err);
      setError(err.message || 'Failed to upload avatar');
      return null;
    }
  };

  const handleSaveProfile = async () => {
    if (!profile) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      let avatarUrl = profile.avatarUrl;

      // Upload avatar if new file selected
      if (avatarFile) {
        const uploadedUrl = await uploadAvatar();
        if (uploadedUrl) {
          avatarUrl = uploadedUrl;
        }
      }

      // Update user profile
      const { error: updateError } = await supabase
        .from('users')
        .update({
          name: formData.name,
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      setSuccess('Profile updated successfully!');
      setEditing(false);
      setAvatarFile(null);

      // Reload profile
      setTimeout(() => {
        loadProfile();
      }, 1000);
    } catch (err: any) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      setError('Please fill in all password fields');
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(passwordData.newPassword);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors.join('. '));
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (updateError) throw updateError;

      setSuccess('Password changed successfully!');
      setPasswordDialogOpen(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err: any) {
      console.error('Error changing password:', err);
      setError(err.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const roleConfig: Record<string, { color: any; label: string }> = {
      admin: { color: 'error', label: 'Admin' },
      hr: { color: 'primary', label: 'HR' },
      recruiter: { color: 'secondary', label: 'Recruiter' }
    };

    const config = roleConfig[role] || { color: 'default', label: role };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ py: 4, px: 2 }}>
        <Container maxWidth="lg">
          <Alert severity="error">Failed to load profile</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            borderRadius: 4
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <PersonIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={700}>
                My Profile
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                Manage your account settings and preferences
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Profile Info */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 4 }}>
              {/* Profile Picture */}
              <Stack direction="row" spacing={3} alignItems="center" mb={4}>
                <Avatar
                  src={avatarPreview}
                  sx={{
                    width: 100,
                    height: 100,
                    fontSize: 36,
                    bgcolor: theme.palette.primary.main,
                    border: `4px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  }}
                >
                  {getInitials(profile.name)}
                </Avatar>

                <Box flex={1}>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    {profile.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    {getRoleBadge(profile.role)}
                    <Chip label={profile.entityName} size="small" variant="outlined" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {profile.email}
                  </Typography>
                </Box>

                {editing && (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    size="small"
                  >
                    Upload Photo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleAvatarChange}
                    />
                  </Button>
                )}
              </Stack>

              <Divider sx={{ my: 3 }} />

              {/* Profile Form */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6" fontWeight={600}>
                    Profile Information
                  </Typography>

                  {!editing ? (
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => setEditing(true)}
                    >
                      Edit Profile
                    </Button>
                  ) : (
                    <Stack direction="row" spacing={1}>
                      <Button
                        startIcon={<CancelIcon />}
                        onClick={() => {
                          setEditing(false);
                          setFormData({ name: profile.name, email: profile.email });
                          setAvatarFile(null);
                          setAvatarPreview(profile.avatarUrl || '');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSaveProfile}
                        disabled={saving}
                      >
                        Save Changes
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={!editing}
                  />

                  <TextField
                    fullWidth
                    label="Email Address"
                    value={formData.email}
                    disabled
                    helperText="Email cannot be changed"
                  />

                  <TextField
                    fullWidth
                    label="Role"
                    value={profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                    disabled
                    helperText="Contact admin to change your role"
                  />

                  <TextField
                    fullWidth
                    label="Organization"
                    value={profile.entityName}
                    disabled
                  />
                </Stack>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Security Section */}
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Security
                </Typography>

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Password
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<LockIcon />}
                      onClick={() => setPasswordDialogOpen(true)}
                    >
                      Change Password
                    </Button>
                  </Box>

                  <Box>
                    {profile.createdAt && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Account Created:</strong>{' '}
                        {new Date(profile.createdAt).toLocaleDateString()}
                      </Typography>
                    )}
                    {profile.lastLoginAt && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Last Login:</strong>{' '}
                        {new Date(profile.lastLoginAt).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column - Activity Stats */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* Activity Stats */}
              <Paper elevation={3} sx={{ p: 3, borderRadius: 4 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Activity Overview
                </Typography>

                <Stack spacing={2} mt={2}>
                  <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                          <WorkIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h4" fontWeight={700}>
                            {stats.jobsCreated}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Jobs Created
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>

                  <Card sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                          <AssignmentIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h4" fontWeight={700}>
                            {stats.applicationsReviewed}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Applications Reviewed
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>

                  {(profile.role === 'admin' || profile.role === 'hr') && (
                    <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="h4" fontWeight={700}>
                              {stats.usersManaged}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Team Members
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </Paper>

              {/* Quick Actions */}
              <Paper elevation={3} sx={{ p: 3, borderRadius: 4 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Quick Actions
                </Typography>

                <Stack spacing={1} mt={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<WorkIcon />}
                    href="/admin/jobs/post"
                  >
                    Post New Job
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AssignmentIcon />}
                    href="/admin/applications"
                  >
                    View Applications
                  </Button>

                  {(profile.role === 'admin' || profile.role === 'hr') && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<PersonIcon />}
                      href="/admin/settings"
                    >
                      Manage Team
                    </Button>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
            />

            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              helperText={`Requirements: ${getPasswordRequirements().join('; ')}`}
              multiline
              FormHelperTextProps={{ sx: { whiteSpace: 'pre-wrap' } }}
            />

            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <LockIcon />}
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
